import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, signUp, signOut } from '@/services/auth';
import toast from 'react-hot-toast';
import LoadingSpinner from '@/components/UI/LoadingSpinner';

const ADMIN_ROLES = ['super_admin', 'admin', 'store_manager', 'regional_manager'];

interface LoginPageProps {
  signInOnly?: boolean; // When true, hides Sign Up and enforces admin-role check
}

const LoginPage: React.FC<LoginPageProps> = ({ signInOnly = false }) => {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp && !signInOnly) {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        if (!formData.phone || !formData.phone.startsWith('+')) {
          toast.error('Phone number must be in international format (e.g., +254712345678)');
          setLoading(false);
          return;
        }
        await signUp(formData.email, formData.password, formData.name, 'sales', formData.phone);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        const user = await signIn(formData.email, formData.password);

        // Staff portal: only admin-level roles may enter
        if (signInOnly && !ADMIN_ROLES.includes(user.role)) {
          await signOut();
          toast.error(`Access denied. Your account (${user.role}) does not have staff access.`);
          setLoading(false);
          return;
        }

        toast.success('Signed in successfully!');
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-emerald-900 text-white flex items-center justify-center font-black text-lg tracking-tight">
            GV
          </div>
          {signInOnly ? (
            <>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Staff Login</h1>
              <p className="text-gray-600">Genvitungu Management Portal</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Genvitungu</h1>
              <p className="text-gray-600">Sales Intelligence &amp; Management Platform</p>
            </>
          )}
        </div>

        <div className="card">
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setIsSignUp(false)}
              className={`flex-1 py-3 font-medium transition-colors ${
                !isSignUp
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
            {!signInOnly && (
              <button
                onClick={() => setIsSignUp(true)}
                className={`flex-1 py-3 font-medium transition-colors ${
                  isSignUp
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign Up
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <>
                <div>
                  <label className="label">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input"
                    required
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="label">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input"
                    required
                    placeholder="+254712345678 (international format)"
                    pattern="\+[0-9]{10,15}"
                  />
                  <p className="text-xs text-gray-500 mt-1">For WhatsApp notifications and contact</p>
                </div>
              </>
            )}

            <div>
              <label className="label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                required
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="input"
                required
                placeholder="Enter your password"
                minLength={6}
              />
            </div>

            {isSignUp && (
              <div>
                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input"
                  required
                  placeholder="Confirm your password"
                  minLength={6}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full py-3 text-lg"
            >
              {loading ? (
                <LoadingSpinner size="small" />
              ) : isSignUp ? (
                'Create Account'
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            {!signInOnly && (
              <p>
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  {isSignUp ? 'Sign in' : 'Sign up'}
                </button>
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>🔒 Secure authentication with Firebase</p>
          <p className="mt-2">© 2026 Onion Trading System</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
